import React, { useEffect, useRef, useState, useCallback } from 'react';
import * as pdfjsLib from 'pdfjs-dist/build/pdf';

pdfjsLib.GlobalWorkerOptions.workerSrc = '/pdf.worker.min.js';

const PdfViewer = ({ src, onLoad }) => {
    const canvasRef = useRef(null);
    const renderTaskRef = useRef(null);
    const pdfRef = useRef(null);
    const [currentPage, setCurrentPage] = useState(1);
    const [totalPages, setTotalPages] = useState(0);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(false);

    useEffect(() => {
        let cancelled = false;
        setLoading(true);
        setError(false);
        pdfjsLib.getDocument({ url: src, cMapPacked: true, withCredentials: false }).promise
            .then(pdfDoc => {
                if (cancelled) return;
                pdfRef.current = pdfDoc;
                setTotalPages(pdfDoc.numPages);
                setCurrentPage(1);
                setLoading(false);
                onLoad?.(pdfDoc.numPages);
            })
            .catch(() => {
                if (!cancelled) { setError(true); setLoading(false); }
            });
        return () => { cancelled = true; };
    }, [src]);

    const renderPage = useCallback(async (pageNum) => {
        const pdf = pdfRef.current;
        const canvas = canvasRef.current;
        if (!pdf || !canvas) return;

        if (renderTaskRef.current) {
            renderTaskRef.current.cancel();
            renderTaskRef.current = null;
        }

        try {
            const page = await pdf.getPage(pageNum);
            const containerWidth = canvas.parentElement?.offsetWidth || 680;
            const baseViewport = page.getViewport({ scale: 1 });
            const scale = containerWidth / baseViewport.width;
            const viewport = page.getViewport({ scale });

            canvas.width = viewport.width;
            canvas.height = viewport.height;

            const task = page.render({ canvasContext: canvas.getContext('2d'), viewport });
            renderTaskRef.current = task;
            await task.promise;
        } catch (e) {
            if (e?.name !== 'RenderingCancelledException') setError(true);
        }
    }, []);

    useEffect(() => {
        if (pdfRef.current && !loading) renderPage(currentPage);
    }, [currentPage, loading, renderPage]);

    const prev = () => setCurrentPage(p => Math.max(1, p - 1));
    const next = () => setCurrentPage(p => Math.min(totalPages, p + 1));

    return (
        <div className="pdf-lv-root" onClick={e => e.stopPropagation()}>
            <div className="pdf-lv-canvas-wrap">
                {loading && (
                    <div className="pdf-lv-loading">
                        <i className="fas fa-spinner fa-spin" /> Loading PDF…
                    </div>
                )}
                {error && !loading && (
                    <div className="pdf-lv-error">
                        <i className="fas fa-exclamation-circle" /> Could not load PDF.{' '}
                        <a href={src} target="_blank" rel="noopener noreferrer">Open directly</a>
                    </div>
                )}
                {!error && <canvas ref={canvasRef} className="pdf-lv-canvas" />}
            </div>

            {!loading && !error && totalPages > 0 && (
                <div className="pdf-lv-bar">
                    <div className="pdf-lv-nav">
                        <button className="pdf-lv-btn" onClick={prev} disabled={currentPage <= 1}>
                            <i className="fas fa-chevron-left" />
                        </button>
                        <span className="pdf-lv-counter">{currentPage} / {totalPages}</span>
                        <button className="pdf-lv-btn" onClick={next} disabled={currentPage >= totalPages}>
                            <i className="fas fa-chevron-right" />
                        </button>
                    </div>
                    <a
                        href={src}
                        download
                        target="_blank"
                        rel="noopener noreferrer"
                        className="pdf-lv-download"
                    >
                        <i className="fas fa-download" /> Download
                    </a>
                </div>
            )}
        </div>
    );
};

export default PdfViewer;
